import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-hot-toast';

// The other half of what Login.tsx linked to but never had: /forgot-password
// resolved to nothing and bounced back to /login. forgotPassword and
// forgotPasswordSubmit were already on useAuth.
//
// Cognito's reset is request-code-then-submit-code, and the second call needs
// the same email as the first, so both steps live in one page.
type Step = 'request' | 'reset';

export default function ForgotPassword() {
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { forgotPassword, forgotPasswordSubmit } = useAuth();
  const navigate = useNavigate();

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast.error('Enter your email address');
      return;
    }

    setLoading(true);
    try {
      const result = await forgotPassword(email);
      if (result.success) {
        setStep('reset');
        toast.success('Check your email for the reset code');
      } else {
        toast.error(result.error || 'Could not start the reset');
      }
    } catch {
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const result = await forgotPasswordSubmit(email, code, password);
      if (result.success) {
        toast.success('Password changed — sign in with your new password');
        navigate('/login');
      } else {
        toast.error(result.error || 'Could not reset the password');
      }
    } catch {
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'appearance-none relative block w-full px-3 py-2.5 border border-gray-300 dark:border-slate-600 rounded-lg placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-primary-600 sm:text-sm';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-primary-600 dark:text-primary-400">
          Celsian AI
        </p>
        <h1 className="mt-3 font-display text-4xl font-medium leading-tight text-gray-900 dark:text-gray-100">
          Reset your password.
        </h1>
        <p className="mt-3 text-gray-600 dark:text-gray-400">
          {step === 'request'
            ? 'We will email you a code to set a new one.'
            : `Enter the code we sent to ${email}.`}
        </p>

        <div className="mt-8 rounded-2xl border border-gray-300/70 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 shadow-[0_24px_70px_rgba(42,39,36,0.07)] p-8">
          {step === 'request' ? (
            <form className="space-y-5" onSubmit={handleRequest}>
              <div>
                <label htmlFor="email" className="block text-sm font-bold text-gray-900 dark:text-gray-100">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={`mt-2 ${inputClass}`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-2.5">
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current" />
                ) : (
                  'Send the code'
                )}
              </button>
            </form>
          ) : (
            <form className="space-y-5" onSubmit={handleReset}>
              <div>
                <label htmlFor="code" className="block text-sm font-bold text-gray-900 dark:text-gray-100">
                  Reset code
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  className={`mt-2 ${inputClass}`}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-bold text-gray-900 dark:text-gray-100">
                  New password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className={`mt-2 ${inputClass}`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">At least 8 characters.</p>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-2.5">
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current" />
                ) : (
                  'Set the new password'
                )}
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-sm text-gray-600 dark:text-gray-400">
          <Link
            to="/login"
            className="font-bold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
          >
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
